"use client";

import type { Variants } from "motion/react";
import type { HTMLAttributes } from "react";

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { motion, useAnimation } from "motion/react";

interface SquarePenIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface SquarePenIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
  loop?: boolean;
  animateOnMount?: boolean;
}

const penVariants: Variants = {
  normal: {
    rotate: 0,
    x: 0,
    y: 0,
  },
  animate: {
    rotate: [-0.5, 0.5, -0.5],
    x: [0, -1, 1.5, 0],
    y: [0, 1.5, -1, 0],
  },
};

const SquarePenIcon = forwardRef<SquarePenIconHandle, SquarePenIconProps>(
  (
    {
      onMouseEnter,
      onMouseLeave,
      className,
      size = 28,
      loop = false,
      animateOnMount = false,
      ...props
    },
    ref,
  ) => {
    const controls = useAnimation();
    const isControlledRef = useRef(false);

    useImperativeHandle(ref, () => {
      isControlledRef.current = true;

      return {
        startAnimation: () => void controls.start("animate"),
        stopAnimation: () => void controls.start("normal"),
      };
    });

    useEffect(() => {
      if ((loop || animateOnMount) && !isControlledRef.current) {
        void controls.start("animate");
      }
    }, [loop, animateOnMount, controls]);

    const handleMouseEnter = useCallback(
      async (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isControlledRef.current && !loop) {
          await controls.start("animate");
        } else {
          onMouseEnter?.(e);
        }
      },
      [controls, onMouseEnter, loop],
    );

    const handleMouseLeave = useCallback(
      async (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isControlledRef.current && !loop) {
          await controls.start("normal");
        } else {
          onMouseLeave?.(e);
        }
      },
      [controls, onMouseLeave, loop],
    );

    return (
      <div
        className={twMerge(className)}
        onMouseEnter={(e) => void handleMouseEnter(e)}
        onMouseLeave={(e) => void handleMouseLeave(e)}
        {...props}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ overflow: "visible" }}
        >
          <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <motion.path
            d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"
            variants={penVariants}
            animate={controls}
            transition={{
              duration: 0.6,
              repeat: loop ? Number.POSITIVE_INFINITY : 0,
              repeatType: loop ? "loop" : undefined,
              repeatDelay: loop ? 1 : 0,
            }}
          />
        </svg>
      </div>
    );
  },
);

SquarePenIcon.displayName = "SquarePenIcon";

export { SquarePenIcon };
